import React, { useState, useEffect, useCallback } from 'react';
import { Lock, RefreshCw, Eye, Clock, AlertCircle, X, ChevronLeft, ChevronRight, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import BBoxAnnotator from '@/components/BBoxAnnotationDialog';
import config from '@/config';

// Review Modal Component
function ReviewModal({ 
  isOpen, 
  onClose, 
  reviewId,
  imageUrl, 
  reviewIndex, 
  totalReviews, 
  onNavigate, 
  lockId, 
  totalPendingCount, 
  userInfo,
  configData
}) {
  const { authenticatedFetch } = useAuth();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showNavConfirm, setShowNavConfirm] = useState(false);
  const [navDirection, setNavDirection] = useState(null);
  const [hasAnnotations, setHasAnnotations] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [bboxKey, setBboxKey] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Debug config data whenever it changes
  useEffect(() => {
    console.log('ReviewModal - configData prop changed:', JSON.stringify(configData, null, 2));
  }, [configData]);

  // Reset bbox component when review changes
  useEffect(() => {
    setBboxKey(prev => prev + 1);
    setHasAnnotations(false);
  }, [reviewId]);

  // Input Focus Detection
  useEffect(() => {
    const handleFocusIn = (e) => {
      const target = e.target;
      if (['INPUT', 'TEXTAREA'].includes(target.tagName) || target.contentEditable === 'true') {
        setIsInputFocused(true);
      }
    };
    const handleFocusOut = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (!active || !['INPUT', 'TEXTAREA'].includes(active.tagName)) {
          setIsInputFocused(false);
        }
      }, 50);
    };

    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  // Prevent spacebar scrolling when typing
  useEffect(() => {
    if (!isInputFocused) return;
    const handleKeyDown = (e) => {
      if (e.code === 'Space') e.stopPropagation();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isInputFocused]);

  // Define performNavigation early so it can be used in callbacks
  const performNavigation = useCallback(async (direction) => {
    setIsNavigating(true);
    setHasAnnotations(false);
    await onNavigate(direction);
    setIsNavigating(false);
  }, [onNavigate]);

  // Define handleConfirmedNavigate with proper dependencies
  const handleConfirmedNavigate = useCallback(() => {
    setShowNavConfirm(false);
    if (navDirection) {
      performNavigation(navDirection);
    }
    setNavDirection(null);
  }, [navDirection, performNavigation]);

  // Global keyboard shortcuts for E (for parent-level confirmations)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in textarea or input
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
        return;
      }

      // E key - Confirm navigation or close dialogs
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        
        // If navigation confirm dialog is open, confirm navigation
        if (showNavConfirm) {
          handleConfirmedNavigate();
        }
        // If close confirm dialog is open, confirm close
        else if (showCloseConfirm) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showNavConfirm, showCloseConfirm, handleConfirmedNavigate, onClose]);

  if (!isOpen) return null;

  const canGoPrevious = reviewIndex > 0;
  const canGoNext = reviewIndex < totalReviews - 1;

  const handleCloseClick = () => {
    if (hasAnnotations) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleNavigateClick = (direction) => {
    if (hasAnnotations) {
      setNavDirection(direction);
      setShowNavConfirm(true);
    } else {
      performNavigation(direction);
    }
  };

  const handleComplete = async (result) => {
    setIsCompleting(true);
    
    console.log("=".repeat(80));
    console.log("REVIEW MODAL - RECEIVED FROM BBOX ANNOTATOR");
    console.log("=".repeat(80));
    console.log("Result from BBoxAnnotator:", JSON.stringify(result, null, 2));
    
    try {
      const payload = {
        node_id: reviewId,
        status: result.status,
        labels_data: result.labels_data,
        comment: result.comment || null
      };
      
      console.log("\nPayload being sent to backend:");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=".repeat(80));

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to complete review');
      }

      const responseData = await response.json();
      console.log("\nBackend response:");
      console.log(JSON.stringify(responseData, null, 2));
      
      setSuccessAction(result.status);
      setShowSuccessMessage(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowSuccessMessage(false);
      
      if (canGoNext) {
        performNavigation('next');
      } else {
        onClose();
      }
      
    } catch (err) {
      console.error('Error completing review:', err);
      alert(`Failed to complete review: ${err.message}`);
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col overflow-hidden">
        
        {/* Top Header Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* Left: Navigation */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleNavigateClick('previous')}
                  disabled={!canGoPrevious || isCompleting || isNavigating}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => handleNavigateClick('next')}
                  disabled={!canGoNext || isCompleting || isNavigating}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              
              <div className="text-sm font-medium">
                <span className="text-gray-900">Item {reviewIndex + 1} of {totalReviews}</span>
                <span className="text-gray-400 mx-2">•</span>
                <span className="text-gray-600">{totalPendingCount} total pending</span>
              </div>
            </div>

            {/* Center: User Info */}
            {userInfo && (
              <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-md border border-indigo-200">
                <span className="text-sm font-medium text-indigo-900">
                  {userInfo.first_name || 'User'}
                </span>
                <span className="text-indigo-300">•</span>
                {userInfo.is_unlimited ? (
                  <span className="text-xs text-indigo-700 font-medium">
                    ✨ Unlimited verifications
                  </span>
                ) : (
                  <span className="text-xs text-indigo-700">
                    <span className="font-semibold">{userInfo.reviews_remaining}</span> remaining
                  </span>
                )}
              </div>
            )}

            {/* Right: Close Button */}
            <button
              onClick={handleCloseClick}
              disabled={isCompleting || isNavigating}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Left Sidebar - Configuration */}
          {configData && (
            <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
              <div className="p-4 space-y-4">
                
                {/* Title */}
                <div className="pb-2 border-b border-gray-300">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                    Requested Configuration
                  </h3>
                </div>
                
                {/* Logo Section */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Logo
                  </div>
                  {configData.needs_logo && configData.logo_data && configData.logo_data.logo_images && configData.logo_data.logo_images.length > 0 ? (
                    <div className="p-2 bg-white rounded-lg border border-gray-300 shadow-sm">
                      <div className="flex flex-wrap gap-2 justify-center">
                        {configData.logo_data.logo_images.map((logo, idx) => (
                          <div key={idx} className="flex flex-col items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                            <div className="w-14 h-14 flex items-center justify-center rounded border-2 border-gray-300 p-1.5" 
                                 style={{ backgroundColor: '#808080' }}>
                              <img 
                                src={logo.url} 
                                alt={`${logo.position} ${logo.color}`}
                                className="max-w-full max-h-full object-contain"
                                title={`${logo.position} - ${logo.color}`}
                              />
                            </div>
                            <div className="text-center">
                              <div className="text-xs font-semibold text-gray-700 capitalize leading-tight">
                                {logo.position.replace('_', ' ')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-white rounded-lg border border-gray-300 text-center">
                      <div className="text-xs text-gray-500">No logo required</div>
                    </div>
                  )}
                </div>

                {/* Lens Section */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Lens Overlay
                  </div>
                  {configData.lens_data && (
                    (configData.lens_data.lens_images && configData.lens_data.lens_images.length > 0) ? (
                      <div className="p-2 bg-white rounded-lg border border-gray-300 shadow-sm">
                        <div className="flex flex-wrap gap-2 justify-center">
                          {configData.lens_data.lens_images.map((lens, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                              <div className="w-14 h-14 flex items-center justify-center bg-white rounded border-2 border-gray-300 overflow-hidden">
                                <img 
                                  src={lens.url} 
                                  alt={lens.type}
                                  className="w-full h-full object-cover"
                                  title={lens.type}
                                />
                              </div>
                              <div className="text-center">
                                <div className="text-xs font-semibold text-gray-700 capitalize leading-tight">
                                  {lens.type}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-white rounded-lg border border-gray-300 text-center">
                        <div className="text-xs text-gray-500">No lens overlay</div>
                      </div>
                    )
                  )}
                  {!configData.lens_data && (
                    <div className="p-3 bg-white rounded-lg border border-gray-300 text-center">
                      <div className="text-xs text-gray-500">No lens overlay</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Right: Image Annotation Area */}
          <div className="flex-1 overflow-hidden relative bg-white">
            {isNavigating ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-4">
                  <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                  <p className="text-gray-600 font-medium">Loading next image...</p>
                </div>
              </div>
            ) : (
              <BBoxAnnotator 
                key={bboxKey}
                imageUrl={imageUrl}
                tags={[
                  { tag: 'lens masking error', color: '#ef4444' },
                  { tag: 'logo error', color: '#f59e0b' },
                  { tag: 'background error', color: '#3b82f6' },
                  { tag: 'lens removal error', color: '#3bf64eff' },
                ]}
                review_lock_id={lockId}
                review_id={reviewId}
                onComplete={handleComplete}
                onAnnotationsChange={setHasAnnotations}
                containerMode={true}
                disableSpaceDrag={isInputFocused}
              />
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Confirmation */}
      {(showCloseConfirm || showNavConfirm) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-orange-600" />
              <h3 className="text-lg font-semibold">Unsaved Changes</h3>
            </div>
            <p className="text-gray-600 mb-6">
              You have unsaved annotations. {showCloseConfirm ? 'Close anyway?' : 'Navigate anyway?'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCloseConfirm(false);
                  setShowNavConfirm(false);
                  if (showNavConfirm) setNavDirection(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => showCloseConfirm ? onClose() : handleConfirmedNavigate()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {showCloseConfirm ? 'Close (E)' : 'Navigate (E)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center animate-in fade-in zoom-in duration-200">
            <CheckCircle className={`w-16 h-16 mb-4 ${successAction === 'accepted' ? 'text-green-600' : 'text-red-600'}`} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Verification {successAction === 'accepted' ? 'Accepted' : 'Rejected'}
            </h3>
            <p className="text-gray-600">
              {canGoNext ? 'Loading next...' : 'All tasks completed!'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


function ReviewManagerFinal() {
  const { authenticatedFetch } = useAuth();
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const [lockingReview, setLockingReview] = useState(null);
  const [currentLockId, setCurrentLockId] = useState(null);
  const [currentConfigData, setCurrentConfigData] = useState(null);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(null);

  // Fetch user info
  const fetchUserInfo = useCallback(async () => {
    setUserInfoLoading(true);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/info/user`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setUserInfo(data);
      if (!data.is_unlimited && data.reviews_remaining !== null && data.reviews_remaining <= 0) {
        setHasReachedLimit(true);
      } else {
        setHasReachedLimit(false);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    } finally {
      setUserInfoLoading(false);
    }
  }, [authenticatedFetch]);

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        status: 'pending',
        page: currentPage,
        page_size: pageSize
      });

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/list?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReviews(data.items);
      setTotalPages(data.total_pages);
      setTotalCount(data.total);
      
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, currentPage, pageSize]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchReviews(), fetchUserInfo()]);
  }, [fetchReviews, fetchUserInfo]);

  // Create lock and open modal
  const handleReviewClick = async (reviewIndex) => {
    const review = reviews[reviewIndex];
    const nodeId = review.node_id;
    
    if (hasReachedLimit) return;
    
    setLockingReview(nodeId);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/${nodeId}/lock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create lock');
      }

      const data = await response.json();
      
      console.log('='.repeat(80));
      console.log('FRONTEND - LOCK RESPONSE RECEIVED');
      console.log('='.repeat(80));
      console.log('Full response:', JSON.stringify(data, null, 2));
      console.log('needs_logo:', data.needs_logo);
      console.log('logo_data:', data.logo_data);
      console.log('lens_data:', data.lens_data);
      console.log('='.repeat(80));
      
      const configData = {
        needs_logo: data.needs_logo,
        logo_data: data.logo_data,
        lens_data: data.lens_data
      };
      
      console.log('Config data being set:', JSON.stringify(configData, null, 2));
      
      setCurrentLockId(data.lock_id);
      setCurrentConfigData(configData);
      setSelectedReviewIndex(reviewIndex);
      setModalOpen(true);
      
    } catch (err) {
      console.error('Error creating lock:', err);
      alert(`Failed to lock review: ${err.message}`);
    } finally {
      setLockingReview(null);
    }
  };

  // Release lock
  const releaseLock = async (nodeId) => {
    if (!nodeId) return;
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/${nodeId}/release`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to release lock');
      }
      
    } catch (err) {
      console.error('Error releasing lock:', err);
    }
  };

  // Close modal and refresh
  const handleModalClose = async () => {
    if (selectedReviewIndex !== null && reviews[selectedReviewIndex]) {
      await releaseLock(reviews[selectedReviewIndex].node_id);
    }
    
    setModalOpen(false);
    setCurrentLockId(null);
    setCurrentConfigData(null);
    setSelectedReviewIndex(null);
    
    // Refresh after closing
    await handleRefresh();
  };

  // Navigate between reviews
  const handleNavigate = async (direction) => {
    // Release current lock
    if (selectedReviewIndex !== null && reviews[selectedReviewIndex]) {
      await releaseLock(reviews[selectedReviewIndex].node_id);
    }
    setCurrentLockId(null);
    setCurrentConfigData(null);

    // Calculate new index
    const newIndex = direction === 'next' 
      ? selectedReviewIndex + 1 
      : selectedReviewIndex - 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= reviews.length) {
      setModalOpen(false);
      setSelectedReviewIndex(null);
      await handleRefresh();
      return;
    }

    const newReview = reviews[newIndex];
    const newNodeId = newReview.node_id;
    
    try {
      // Lock new review
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/${newNodeId}/lock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create lock');
      }

      const data = await response.json();
      
      console.log('='.repeat(80));
      console.log('FRONTEND - NAVIGATE LOCK RESPONSE');
      console.log('='.repeat(80));
      console.log('Full response:', JSON.stringify(data, null, 2));
      console.log('needs_logo:', data.needs_logo);
      console.log('logo_data:', data.logo_data);
      console.log('lens_data:', data.lens_data);
      console.log('='.repeat(80));
      
      const configData = {
        needs_logo: data.needs_logo,
        logo_data: data.logo_data,
        lens_data: data.lens_data
      };
      
      console.log('Config data being set:', JSON.stringify(configData, null, 2));
      
      setCurrentLockId(data.lock_id);
      setCurrentConfigData(configData);
      setSelectedReviewIndex(newIndex);
      // DO NOT REFRESH HERE - keep reviews array stable
      
    } catch (err) {
      console.error('Error locking new review:', err);
      alert(`Failed to lock review: ${err.message}`);
      setModalOpen(false);
      setSelectedReviewIndex(null);
      await handleRefresh();
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchUserInfo();
  }, [fetchReviews, fetchUserInfo]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-8">
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Dashboard Header */}
          {userInfoLoading ? (
            <div className="mb-8 p-6 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin" />
              </div>
            </div>
          ) : userInfo ? (
            <div className="mb-8 p-6 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">
                    Ciao, {userInfo.first_name || 'User'}!
                  </h1>
                  {userInfo.is_unlimited ? (
                    <p className="text-lg text-indigo-100">
                      ✨ You have <span className="font-bold text-white">unlimited verifications</span> available out of <span className="font-bold text-white">{totalCount} total</span>
                    </p>
                  ) : (
                    <p className="text-lg text-indigo-100">
                      You have <span className="font-bold text-white">{userInfo.reviews_remaining}</span> remaining out of <span className="font-bold text-white">{totalCount} total</span>
                    </p>
                  )}
                </div>
                {hasReachedLimit && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-white/20 backdrop-blur-sm rounded-lg border-2 border-white/30">
                    <AlertCircle className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-bold text-lg">Limit Reached</p>
                      <p className="text-sm text-indigo-100">No more verifications allowed</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-8 p-6 bg-gradient-to-r from-gray-600 to-gray-500 rounded-xl shadow-lg text-white">
              <h1 className="text-3xl font-bold mb-2">Verification Manager</h1>
              <p className="text-gray-200">Loading user information...</p>
            </div>
          )}

          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {reviews.length} of {totalCount} items
            </p>
            <button
              onClick={handleRefresh}
              disabled={loading || userInfoLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${(loading || userInfoLoading) ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading data</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Reviews Grid */}
          {loading && reviews.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No pending verifications found</p>
            </div>
          ) : (
            <div className={`space-y-4 ${hasReachedLimit ? 'opacity-40 pointer-events-none' : ''}`}>
              {reviews.map((review, index) => {
                const isDisabled = hasReachedLimit;
                const isProcessing = lockingReview === review.node_id;
                
                return (
                  <div
                    key={review.node_id}
                    className={`border-2 rounded-lg p-3 transition-all ${
                      isDisabled
                        ? 'border-gray-300 bg-gray-100'
                        : 'border-gray-200 bg-white hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Image Preview */}
                      {review.image_url ? (
                        <div className="flex-shrink-0">
                          <img
                            src={review.image_url}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300"
                          />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                          <Eye className="w-6 h-6 text-gray-400" />
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex-1 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">
                            {formatDate(review.created_at)}
                          </p>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                            <Clock className="w-3 h-3" />
                            Pending Verification
                          </span>
                        </div>

                        {/* Review Button */}
                        <button
                          onClick={() => handleReviewClick(index)}
                          disabled={isDisabled || isProcessing}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                            isDisabled
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          } disabled:opacity-50`}
                        >
                          {isProcessing ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Locking...
                            </>
                          ) : hasReachedLimit ? (
                            <>
                              <Lock className="w-4 h-4" />
                              Limit Reached
                            </>
                          ) : (
                            'Start Verification'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Previous
              </button>
              
              <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md font-medium">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {modalOpen && selectedReviewIndex !== null && reviews[selectedReviewIndex] && (
        <ReviewModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          reviewId={reviews[selectedReviewIndex].node_id}
          imageUrl={reviews[selectedReviewIndex].image_url}
          lockId={currentLockId}
          reviewIndex={selectedReviewIndex}
          totalReviews={reviews.length}
          onNavigate={handleNavigate}
          totalPendingCount={totalCount}
          userInfo={userInfo}
          configData={currentConfigData}
        />
      )}
    </div>
  );
}

export default ReviewManagerFinal;